import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import {
    CatchPriceListRequest, CatchPriceListsClient,
    ContractCePointRequest,
    ContractCePointsClient,
    ContractCeRateRequest,
    ContractCeRatesClient,
    ContractCostRequest,
    ContractCostsClient,
    ContractRateRequest, ContractRatesClient,
    ContractShareRequest, ContractSharesClient,
    ContractWorkByRequest, ContractWorkBysClient,
    VesselContractRequest, VesselContractResponse, VesselContractsClient
} from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { setToNewZealandTimeZone } from 'src/utility/helper/dateHelper';

export interface Contract extends VesselContractResponse { }

export interface ContractModel extends VesselContractRequest {
    vesselContractId: number;
    copyDataFromContractId: number;
}

export interface State {
    isLoading: boolean;
    error: boolean;
    contractList: Contract[];
    contract: Contract | null;
    notFound: boolean;
}

const initialState: State = {
    isLoading: true,
    error: false,
    contractList: [],
    contract: null,
    notFound: false,
};

const contractSlice = createSlice({
    name: 'contract',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.isLoading = false;
            state.notFound = false;
            state.error = action.payload;
        },
        getContractListSuccess: (state, action: PayloadAction<Contract[]>) => {
            state.isLoading = false;
            state.contractList = action.payload;
        },
        getContractSuccess: (state, action: PayloadAction<Contract>) => {
            state.isLoading = false;
            state.contract = action.payload;
        },
        getContractFailure: (state) => {
            state.isLoading = false;
            state.contract = null;
        },
        getContractNotFound: (state) => {
            state.isLoading = false;
            state.notFound = true;
        },
        addNewContractSuccess: (state, action: PayloadAction<Contract>) => {
            const newContract = action.payload;
            state.contractList = [newContract, ...state.contractList];
            state.isLoading = false;
        },
        updateContractSuccess: (state, action: PayloadAction<Contract>) => {
            const updatedItem = action.payload;
            const index = state.contractList.findIndex(item => item.vesselContractId === updatedItem.vesselContractId);
            state.contractList = [...state.contractList.slice(0, index), updatedItem, ...state.contractList.slice(index + 1)];
            state.isLoading = false;
        },
        deleteContractSuccess: (state, action: PayloadAction<number>) => {
            const vesselContractId = action.payload;
            state.contractList = state.contractList.filter(item => item.vesselContractId !== vesselContractId);
            toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
            state.isLoading = false;
        },
    },
});

// Reducer
export default contractSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getContractSuccess,
} = contractSlice.actions;

// for useSelector
export const selectContract = (state: RootState) => state.contract as State;

// ----------------------------------------------------------------------

export function getAllContracts(validToDate?: Date) {
    return async (dispatch, getState) => {
        dispatch(contractSlice.actions.startLoading());
        try {
            const client = new VesselContractsClient(undefined, axios);
            let response = await client.getAllVesselContracts(validToDate);
            dispatch(contractSlice.actions.getContractListSuccess(response));
        } catch (error) {
            dispatch(contractSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function getAllPersonalContracts(validToDate?: Date) {
    return async (dispatch, getState) => {
        dispatch(contractSlice.actions.startLoading());
        try {
            const client = new VesselContractsClient(undefined, axios);
            let response = await client.getAllPersonalContracts(validToDate);
            dispatch(contractSlice.actions.getContractListSuccess(response));
        } catch (error) {
            dispatch(contractSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function getPersonalAndVesselContracts(validToDate?: Date){
    return async (dispatch, getState) => {
        dispatch(contractSlice.actions.startLoading());
        try {
            const client = new VesselContractsClient(undefined, axios);
            let responsePersonal = await client.getAllPersonalContracts(validToDate);
            let responseVessel = await client.getAllVesselContracts(validToDate);
            dispatch(contractSlice.actions.getContractListSuccess(responsePersonal.concat(responseVessel)));
        } catch (error) {
            dispatch(contractSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function getContract(id: number) {
    return async (dispatch, getState) => {
        dispatch(contractSlice.actions.startLoading());
        try {
            const client = new VesselContractsClient(undefined, axios);
            const response = await client.get(id);
            dispatch(contractSlice.actions.getContractSuccess(response));
            return response;
        } catch (error: any) {
            dispatch(contractSlice.actions.getContractFailure());
            if ((error as AxiosError) && error.status === 404) {
                dispatch(contractSlice.actions.getContractNotFound());
            } else {
                handleError(error);
            }
        }
    };
}

export function createContract(contract: ContractModel, copyCatchPriceList: boolean,
    copyRates: boolean, copyShares: boolean, copyCosts: boolean, copyCeRates: boolean, copyCePoints: boolean, copyWorkBys: boolean) {
    async function copyContractDetails(copyDataFromContractId: number, newContractId: number, copyCatchPriceList: boolean,
        copyRates: boolean, copyShares: boolean, copyCosts: boolean, copyCeRates: boolean, copyCePoints: boolean, copyWorkBys: boolean) {
        const client = new VesselContractsClient(undefined, axios);
        const copyFromContract = await client.get(copyDataFromContractId);
        if (copyFromContract) {
            if (copyCatchPriceList) {
                const catchPriceListClient = new CatchPriceListsClient(undefined, axios);
                for (const copyCatchPriceList of copyFromContract.catchPriceList) {
                    const request = {
                        vesselContractId: newContractId,
                        productId: copyCatchPriceList.productId,
                        productSpeciesId: copyCatchPriceList.productSpeciesId,
                        productQualityId: copyCatchPriceList.productQualityId,
                        productStateId: copyCatchPriceList.productStateId,
                        productSizeId: copyCatchPriceList.productSizeId,
                        ratePerKg: copyCatchPriceList.ratePerKg
                    } as CatchPriceListRequest;
                    await catchPriceListClient.create(request);
                }
            }

            if (copyRates) {
                const contractRatesClient = new ContractRatesClient(undefined, axios);
                for (const copyContractRate of copyFromContract.contractRate) {
                    const request = {
                        vesselContractId: newContractId,
                        positionId: copyContractRate.positionId,
                        interimDailyRate: copyContractRate.interimDailyRate,
                        interimPayPercentage: copyContractRate.interimPayPercentage,
                        dailyRate: copyContractRate.dailyRate
                    } as ContractRateRequest;
                    await contractRatesClient.create(request);
                }
            }

            if (copyShares) {
                const contractSharesClient = new ContractSharesClient(undefined, axios);
                for (const copyContractShare of copyFromContract.contractShare) {
                    const request = {
                        vesselContractId: newContractId,
                        positionId: copyContractShare.positionId,
                        share: copyContractShare.share,
                        costShare: copyContractShare.costShare,
                        minShare: copyContractShare.minShare,
                        maxShare: copyContractShare.maxShare,
                    } as ContractShareRequest;
                    await contractSharesClient.create(request);
                }
            }

            if (copyCosts) {
                const contractCostsClient = new ContractCostsClient(undefined, axios);
                for (const copyContractCost of copyFromContract.contractCost) {
                    const request = {
                        vesselContractId: newContractId,
                        description: copyContractCost.description,
                        unitPrice: copyContractCost.unitPrice,
                    } as ContractCostRequest;
                    await contractCostsClient.create(request);
                }
            }

            if (copyCeRates) {
                const client = new ContractCeRatesClient(undefined, axios);
                const copyFromContractCeRates = await client.getByVesselContractId(copyDataFromContractId);
                for (const copyFromContractCeRate of copyFromContractCeRates) {
                    const request = {
                        vesselContractId: newContractId,
                        minPoints: copyFromContractCeRate.minPoints,
                        maxPoints: copyFromContractCeRate.maxPoints,
                        rate: copyFromContractCeRate.rate
                    } as ContractCeRateRequest;
                    await client.create(request);
                }
            }

            if (copyCePoints) {
                const client = new ContractCePointsClient(undefined, axios);
                const copyFromContractCePoints = await client.getByVesselContractId(copyDataFromContractId);
                for (const copyFromContractCePoint of copyFromContractCePoints) {
                    const request = {
                        vesselContractId: newContractId,
                        minDays: copyFromContractCePoint.minDays,
                        maxDays: copyFromContractCePoint.maxDays,
                        points: copyFromContractCePoint.points
                    } as ContractCePointRequest;
                    await client.create(request);
                }
            }

            if (copyWorkBys) {
                const contractWorkBysClient = new ContractWorkBysClient(undefined, axios);
                for (const copyContractWorkBy of copyFromContract.contractWorkBy) {
                    const request = {
                        vesselContractId: newContractId,
                        positionId: copyContractWorkBy.positionId,
                        description: copyContractWorkBy.description,
                        amount: copyContractWorkBy.amount
                    } as ContractWorkByRequest;
                    await contractWorkBysClient.create(request);
                }
            }
        }
    }

    return async (dispatch, getState) => {
        dispatch(contractSlice.actions.startLoading());
        try {
            const { vesselContractId, copyDataFromContractId, ...payload } = contract;
            const request = payload as VesselContractRequest;
            request.validFromDate = setToNewZealandTimeZone(request.validFromDate);
            request.validToDate = setToNewZealandTimeZone(request.validToDate);
            const client = new VesselContractsClient(undefined, axios);
            const response = await client.create(request);
            dispatch(contractSlice.actions.addNewContractSuccess(response));

            const newContractId = response.vesselContractId;
            if (copyDataFromContractId > 0) {
                await copyContractDetails(copyDataFromContractId, newContractId, copyCatchPriceList, copyRates, copyShares, copyCosts, copyCeRates, copyCePoints, copyWorkBys);
            }

            toast.success(`${response.contractNumber} created successfully!`);
        } catch (error) {
            dispatch(contractSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateContract(contract: ContractModel, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(contractSlice.actions.startLoading());
        try {
            const { vesselContractId, copyDataFromContractId, ...payload } = contract;
            const request = payload as VesselContractRequest;
            request.validFromDate = setToNewZealandTimeZone(request.validFromDate);
            request.validToDate = setToNewZealandTimeZone(request.validToDate);
            const client = new VesselContractsClient(undefined, axios);
            const response = await client.edit(vesselContractId, request);
            dispatch(contractSlice.actions.updateContractSuccess(response));

            if (showNotification) {
                toast.success(`${response.contractNumber} ${response.isDeleted ? 'deleted' : 'updated'} successfully!`);
            }
        } catch (error) {
            dispatch(contractSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

////////////////////////////////////////////////////////////
export async function getAllContractList() {
    try {
        const client = new VesselContractsClient(undefined, axios);
        const response = await client.getAllVesselContracts();
        return response.filter(r => !r.isDeleted);
    } catch (error) {
        handleError(error);
        return [];
    }
}

export async function getAllPersonalContractList() {
    try {
        const client = new VesselContractsClient(undefined, axios);
        const response = await client.getAllPersonalContracts();
        return response.filter(r => !r.isDeleted);
    } catch (error) {
        handleError(error);
        return [];
    }
}

export async function getAllPersonalContractsForVessel(vesselId: number) {
    try {
        const contractsClient = new VesselContractsClient(undefined, axios);
        return await contractsClient.getAllPersonalContractsForVessel(vesselId);
    } catch (error) {
        handleError(error);
        return [];
    }
}
